import gql from 'graphql-tag';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import ITimeRange from 'types/TimeRange';

export interface IDailySignatureAggregatedInfo {
  injuryCount: number;
  inaccurateTimeCount: number;
  breakPolicyCount: number;
  customFlaggedCount: number;
  missedSignOffs: number;
}

export interface IDailySignatureResolvedViewStatus {
  hideProcessedInjuries: boolean;
  hideResolvedInaccuracies: boolean;
  hideResolvedBreaks: boolean;
  hideResolvedCustomQuestions: boolean;
}

const DAILY_SIGNATURE_ALERTS_COUNTS_QUERY = gql`
  query DailySignatureAlertsCounts(
    $startTime: Timestamp!
    $endTime: Timestamp!
    $hideProcessedInjuries: Boolean!
    $hideResolvedInaccuracies: Boolean!
    $hideResolvedBreaks: Boolean!
    $hideResolvedCustomQuestions: Boolean!
  ) {
    dashboard {
      dailySignatureAlertsCounts(
        startTime: $startTime
        endTime: $endTime
        hideProcessedInjuries: $hideProcessedInjuries
        hideResolvedInaccuracies: $hideResolvedInaccuracies
        hideResolvedBreaks: $hideResolvedBreaks
        hideResolvedCustomQuestions: $hideResolvedCustomQuestions
      ) {
        injuryCount
        inaccurateTimeCount
        breakPolicyCount
        customFlaggedCount
        missedSignOffs
      }
    }
  }
`;

export default function useDailySignatureAlertsStatus(
  timeRange: ITimeRange<DateTime>,
  viewStatus: IDailySignatureResolvedViewStatus,
  enabled: boolean
) {
  const graphQLClient = useGraphQLClient();
  const [error, setError] = useState<string | null>(null);
  const [aggregatedInfo, setAggregatedInfo] = useState<IDailySignatureAggregatedInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [refetch, setRefetch] = useState<boolean>(false);

  const startTime = timeRange.startTime.toISO({ suppressMilliseconds: true, includeOffset: false });
  const endTime = timeRange.endTime.toISO({ suppressMilliseconds: true, includeOffset: false });

  const { hideProcessedInjuries, hideResolvedInaccuracies, hideResolvedBreaks, hideResolvedCustomQuestions } =
    viewStatus;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    graphQLClient
      .request<{ dashboard: { dailySignatureAlertsCounts: IDailySignatureAggregatedInfo } }>({
        document: DAILY_SIGNATURE_ALERTS_COUNTS_QUERY,
        variables: {
          startTime,
          endTime,
          hideProcessedInjuries,
          hideResolvedInaccuracies,
          hideResolvedBreaks,
          hideResolvedCustomQuestions,
        },
      })
      .then((result) => {
        if (cancelled) return;
        setAggregatedInfo(result.dashboard.dailySignatureAlertsCounts);
        setError(null);
        setIsLoading(false);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(String(requestError));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    startTime,
    endTime,
    hideProcessedInjuries,
    hideResolvedInaccuracies,
    hideResolvedBreaks,
    hideResolvedCustomQuestions,
    refetch,
    graphQLClient,
  ]);

  return { aggregatedInfo, error, isLoading, setRefetch, refetch };
}
